import { useEffect, useState, type FormEvent } from 'react'
import { ArrowRight, Loader2, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface TicketTransferDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (recipientEmail: string) => Promise<void>
  submitting: boolean
  error: string | null
}

export function TicketTransferDialog({
  open,
  onOpenChange,
  onSubmit,
  submitting,
  error,
}: TicketTransferDialogProps) {
  const [recipientEmail, setRecipientEmail] = useState('')

  useEffect(() => {
    if (!open) setRecipientEmail('')
  }, [open])

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const email = recipientEmail.trim().toLowerCase()
    if (!email) return
    await onSubmit(email)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Transfer this ticket
          </DialogTitle>
          <DialogDescription>
            We&apos;ll email a secure, single-use claim link. You remain the owner until the
            recipient claims it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ticket-transfer-recipient">Recipient email</Label>
            <Input
              id="ticket-transfer-recipient"
              type="email"
              value={recipientEmail}
              onChange={(event) => setRecipientEmail(event.target.value)}
              placeholder="recipient@example.com"
              autoComplete="email"
              required
              disabled={submitting}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              The recipient can claim as a guest or optionally sign in. The QR code rotates after
              the claim.
            </p>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !recipientEmail.trim()}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              {submitting ? 'Sending…' : 'Send claim link'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
